import React, { useState } from 'react';
import { css } from '@emotion/react';

// components
import { Body } from '@leafygreen-ui/typography';
import Icon from '@leafygreen-ui/icon';
import Button from '@leafygreen-ui/button';
import { Menu, MenuItem, MenuSeparator } from '@leafygreen-ui/menu';
import VerifyPasscodeInput from 'js/common/components/MultiFactorAuth/VerifyPasscodeInput';
import VerifyEmailFactor from 'js/common/components/MultiFactorAuth/VerifyEmailFactor';
import VerifyPushFactor from 'js/common/components/MultiFactorAuth/VerifyPushFactor';
import VerifyAuthPushFactor from 'js/common/components/MultiFactorAuth/VerifyAuthPushFactor';
import VerifyWebAuthnFactor from 'js/common/components/MultiFactorAuth/VerifyWebAuthnFactor';
// import VerifyVoiceFactor from 'js/common/components/MultiFactorAuth/VerifyVoiceFactor';
import VerifySmsFactor from 'js/common/components/MultiFactorAuth/VerifySmsFactor';

// common styles
import { baseStyle, bodyParagraphStyles } from 'js/common/styles/multiFactorAuth';

// types
import { OktaMfaFactor, FactorType } from 'js/common/types/accountMultiFactorAuth';
import { Search } from 'js/auth/types/search';

import { factorMetadata } from 'js/common/utils/multiFactorAuthUtils';

const dropdownStyles = {
  menu: css({
    width: '210px',
  }),
  vercelMenu: css({
    width: '350px',
  }),
  button: css({
    zIndex: 5,
    width: '210px',
  }),
  vercelButton: css({
    zIndex: 5,
    width: '350px',
  }),
  buttonText: css({
    fontWeight: 'bold',
    display: 'inline-flex',
    justifyContent: 'space-between',
    width: '100%',
  }),
  menuItem: css({
    padding: '20px 32px',
  }),
};

interface VerifySelectedFactorProps {
  availableFactors: Array<OktaMfaFactor>;
  clientState?: Search;
  onSuccess: (response?) => void;
  isDeleteFlow?: boolean;
  isVercelIntegration?: boolean;
}

export default function VerifySelectedFactor({
  availableFactors,
  clientState = {},
  onSuccess,
  isDeleteFlow,
}: VerifySelectedFactorProps) {
  const [selectedFactor, setSelectedFactor] = useState<OktaMfaFactor>(availableFactors[0]);
  const { isVercelIntegration, stateToken } = clientState;
  const isAuthFlow = !!stateToken;

  const renderFactorVerification = () => {
    switch (selectedFactor.factorType) {
      case FactorType.Email: {
        return (
          <VerifyEmailFactor
            emailFactor={selectedFactor}
            clientState={clientState}
            onSuccess={onSuccess}
            isDeleteFlow={isDeleteFlow}
          />
        );
      }
      case FactorType.Push: {
        return isAuthFlow ? (
          <VerifyAuthPushFactor pushFactor={selectedFactor} clientState={clientState} onSuccess={onSuccess} />
        ) : (
          <VerifyPushFactor pushFactor={selectedFactor} onSuccess={onSuccess} isDeleteFlow={isDeleteFlow} />
        );
      }
      case FactorType.TokenSoftwareTotp: {
        return (
          <>
            <Body css={bodyParagraphStyles}>
              An authentication code has been sent to your device. Enter the code to continue and be redirected.
            </Body>
            <VerifyPasscodeInput
              inputType="code"
              factor={selectedFactor}
              clientState={clientState}
              onSuccess={onSuccess}
            />
          </>
        );
      }
      case FactorType.Call: {
        return (
          <Body css={bodyParagraphStyles}>
            Voice Factor is currently disabled, please authenticate with another factor.
          </Body>
          // return <VerifyVoiceFactor voiceFactor={selectedFactor} clientState={clientState} onSuccess={onSuccess} />;
        );
      }
      case FactorType.Sms: {
        return (
          <VerifySmsFactor
            smsFactor={selectedFactor}
            clientState={clientState}
            onSuccess={onSuccess}
            isDeleteFlow={isDeleteFlow}
          />
        );
      }
      case FactorType.WebAuthn: {
        return (
          <VerifyWebAuthnFactor
            webAuthnFactor={selectedFactor}
            clientState={clientState}
            onSuccess={onSuccess}
            isDeleteFlow={isDeleteFlow}
          />
        );
      }
      default: {
        // do nothing
        return null;
      }
    }
  };

  return (
    <>
      <div css={baseStyle.text}>
        <Body css={bodyParagraphStyles}>
          <b>Authentication Method</b>
        </Body>
        <MenuDropdown
          isVercelIntegration={isVercelIntegration}
          options={availableFactors}
          setSelected={setSelectedFactor}
          selectedFactor={selectedFactor}
        />
      </div>
      {renderFactorVerification()}
    </>
  );
}

/*
 React re-renders the entire component on state change. This is an issue with
 a controlled menu dropdown, as opening/closing the dropdown will update the
 isDropdownOpen state and cause the component to re-render and reset state
 in the nested factor verification components. The solution is to break out the
 dropdown into its own component.
*/
interface MenuDropdownProps {
  options: Array<OktaMfaFactor>;
  setSelected: (factor) => void;
  selectedFactor: OktaMfaFactor;
  isVercelIntegration?: boolean;
}

export function MenuDropdown({ isVercelIntegration, options, setSelected, selectedFactor }: MenuDropdownProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const getDisplayName = (factor) => {
    return factorMetadata[factor.factorType].name;
  };

  return (
    <Menu
      align="bottom"
      justify="start"
      trigger={
        <Button css={isVercelIntegration ? dropdownStyles.vercelButton : dropdownStyles.button}>
          <div css={dropdownStyles.buttonText}>
            {getDisplayName(selectedFactor)}
            <Icon glyph="CaretDown" />
          </div>
        </Button>
      }
      css={isVercelIntegration ? dropdownStyles.vercelMenu : dropdownStyles.menu}
      setOpen={setIsDropdownOpen}
      open={isDropdownOpen}
    >
      {options.map((factor) => (
        <React.Fragment key={factor.id}>
          <MenuItem
            css={dropdownStyles.menuItem}
            active={factor.id === selectedFactor.id}
            onClick={() => {
              setSelected(factor);
              setIsDropdownOpen(false);
            }}
            data-testid={`menu-item-${factor.id}`}
          >
            {getDisplayName(factor)}
          </MenuItem>
          <MenuSeparator />
        </React.Fragment>
      ))}
    </Menu>
  );
}
